/* eslint-disable jsx-a11y/label-has-associated-control */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ExternalLink,
  Eye,
  Flag,
  Loader2,
  MapPin,
  Phone,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// We import the form value type from the existing EditCheckInForm to avoid duplication.
import { AddProviderModal } from '@/components/provider-directory/AddProviderModal';
import { useTreatmentCheckInPush } from '@/components/treatment/check-ins/hooks/useTreatmentCheckInPush';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Subheading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useMatter } from '@/hooks/useMatter';
import { useTelephonyProvider } from '@/hooks/useTelephonyProvider';
import { useInitiateCallMutation } from '@/services/api/dialpadService';
import { usePromptPushNote } from '@/contexts/PushNoteToCmsContext';
import { useResolveExternalNoteQuery } from '@/services/api/externalNoteService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import {
  useGetRCOAuthStatusQuery,
  useInitiateRCCallMutation,
} from '@/services/api/ringcentralService';
import {
  useAddProviderUpdateToCheckInMutation,
  useApproveMedicalProviderMutation,
  useFinalizeTreatmentCheckInMutation,
  useGenerateCheckInObjectivesMutation,
  useGetMedicalProvidersQuery,
  useGetReviewFoundProvidersQuery,
  useRejectMedicalProviderMutation,
} from '@/services/api/treatmentService';
import {
  EncounterSchema,
  ProviderTreatmentStatus,
  ReviewFoundProviderEntry,
  TreatmentCheckIn,
  TreatmentCheckInStatus,
} from '@/services/types/treatment-types';

import { FlagIssueModal } from './FlagIssueModal';
import { useAutoSaveCheckInDraft } from './hooks';
import { LastCheckInOverview } from './LastCheckInOverview';
import { ObjectivesSection } from './ObjectivesSection';
import { PainLevelSelector } from './PainLevelSelector';
import {
  apiObjectiveToFormObjective,
  apiProviderUpdateToFormProviderUpdate,
  checkInFormSchema,
  EditCheckInFormValues,
  formObjectiveToApiObjective,
  formProviderUpdateToApiProviderUpdate,
  mergeServerObjectives,
} from './types';

function buildCheckInContext(
  values: EditCheckInFormValues,
): string {
  const parts: string[] = [];
  parts.push('Treatment Check-in Summary');
  if (values.pain_level != null && values.pain_level !== -1) {
    parts.push(`Pain level: ${values.pain_level}/10`);
  } else if (values.pain_level === -1) {
    parts.push('Pain level: Could not assess');
  }
  if (values.provider_updates.length > 0) {
    parts.push('Provider updates:');
    values.provider_updates.forEach((pu) => {
      const name = pu.newProviderName ?? 'Unknown provider';
      const status = pu.newTreatmentStatus ?? 'unknown status';
      parts.push(`- ${name}: ${status}`);
      if (pu.newTreatmentNotes?.trim()) {
        parts.push(`  Notes: ${pu.newTreatmentNotes}`);
      }
    });
  }
  if (values.objectives.length > 0) {
    parts.push('Objectives:');
    values.objectives.forEach((obj) => {
      const response = obj.response ?? 'no response';
      parts.push(`- ${obj.label}: ${response}`);
    });
  }
  if (values.general_notes?.trim()) {
    parts.push(`General notes: ${values.general_notes}`);
  }
  return parts.join('\n');
}

interface EditCheckInFormProps {
  checkIn: TreatmentCheckIn;
  onCancel: () => void;
}

export const EditCheckInForm = ({
  checkIn,
  onCancel,
}: EditCheckInFormProps) => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const [isAddProviderModalOpen, setIsAddProviderModalOpen] = useState(false);
  const [isFlagIssueModalOpen, setIsFlagIssueModalOpen] = useState(false);
  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const { isRingCentral } = useTelephonyProvider();
  const { data: rcOAuthStatus } = useGetRCOAuthStatusQuery(undefined, {
    skip: !isRingCentral,
  });
  const isRCConnected = !isRingCentral || (rcOAuthStatus?.connected ?? false);
  const { data: incidents } = useGetIncidentsQuery(
    { matterId },
    { skip: !isAddProviderModalOpen },
  );
  const { data: medicalProviders = [] } = useGetMedicalProvidersQuery(matterId);
  const [addProviderUpdate] = useAddProviderUpdateToCheckInMutation();
  const [finalizeCheckIn] = useFinalizeTreatmentCheckInMutation();
  const { data: reviewFoundProviders = [] } = useGetReviewFoundProvidersQuery({
    matterId,
    limit: 50,
  });
  const [approveMedicalProvider, { isLoading: isApprovingProvider }] =
    useApproveMedicalProviderMutation();
  const [rejectMedicalProvider, { isLoading: isRejectingProvider }] =
    useRejectMedicalProviderMutation();
  const { pushCheckInChanges, hasLinkedExternalTasks } =
    useTreatmentCheckInPush({ matterId });

  // CMS resolution + push-note context for post-finalization flow
  const { data: cmsResolution } = useResolveExternalNoteQuery({ matterId });
  const { promptPushNote } = usePromptPushNote();
  const [initiateDialpadCall, { isLoading: isInitiatingDialpadCall }] =
    useInitiateCallMutation();
  const [initiateRCCall, { isLoading: isInitiatingRCCall }] =
    useInitiateRCCallMutation();
  const isInitiatingCall = isInitiatingDialpadCall || isInitiatingRCCall;
  const [generateObjectives, { isLoading: isGeneratingObjectives }] =
    useGenerateCheckInObjectivesMutation();

  const incidentCenter = useMemo(() => {
    const list = incidents ?? [];
    const best =
      list.find(
        (i) =>
          i.normalized_location?.latitude != null &&
          i.normalized_location?.longitude != null,
      ) ??
      list.find(
        (i) => i.extracted_latitude != null && i.extracted_longitude != null,
      );
    if (!best) return null;
    const lat = best.normalized_location?.latitude ?? best.extracted_latitude;
    const lng = best.normalized_location?.longitude ?? best.extracted_longitude;
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [incidents]);

  const form = useForm<EditCheckInFormValues>({
    resolver: zodResolver(checkInFormSchema),
    defaultValues: {
      general_notes: checkIn.general_notes,
      pain_level: checkIn.pain_level ?? null,
      pain_level_note: checkIn.pain_level_note ?? '',
      provider_updates:
        checkIn.provider_updates
          ?.filter((p) => Boolean(p.provider_id))
          .map((p) => apiProviderUpdateToFormProviderUpdate(p)) || [],
      objectives:
        checkIn.objectives?.map(apiObjectiveToFormObjective) ?? [],
    },
  });

  // Validation error keys — kept for child component API compatibility.
  const validationErrors = useMemo(() => new Set<string>(), []);

  // Old draft migration: generate objectives if no objectives exist
  useEffect(() => {
    if (checkIn.status !== TreatmentCheckInStatus.DRAFT) {
      return;
    }
    if (checkIn.objectives && checkIn.objectives.length > 0) return;

    generateObjectives({ matterId, checkInId: checkIn.id })
      .unwrap()
      .then((newObjectives) => {
        replaceObjectives(
          newObjectives.map(apiObjectiveToFormObjective),
        );
      })
      .catch(() => {
        // Generation may fail for various reasons; form stays with empty objectives
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn.id]);

  // Sync newly-generated objectives (e.g. after adding a provider mid-draft)
  // into the form without clobbering existing user-entered responses.
  useEffect(() => {
    if (!checkIn.objectives?.length) return;

    const merged = mergeServerObjectives(
      form.getValues('objectives'),
      checkIn.objectives,
    );

    // Only call replace when there are genuinely new entries
    if (merged.length > form.getValues('objectives').length) {
      replaceObjectives(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn.objectives]);

  const { isSaving } = useAutoSaveCheckInDraft({
    form,
    matterId,
    checkInId: checkIn.id,
  });

  const {
    fields: providerUpdateFields,
    append: appendProviderUpdate,
    remove: removeProviderUpdate,
  } = useFieldArray({
    control: form.control,
    name: 'provider_updates',
  });

  const {
    fields: objectiveFields,
    replace: replaceObjectives,
    remove: removeObjective,
  } = useFieldArray({
    control: form.control,
    name: 'objectives',
  });

  const handleAddProvider = () => {
    setIsAddProviderModalOpen(true);
  };

  const viewCheckIn = () => {
    navigate(`/matters/${matterId}/treatment/check-ins/${checkIn.id}`);
  };

  const onSubmit = async (values: EditCheckInFormValues) => {
    await finalizeAndNavigate(values);
  };

  const finalizeAndNavigate = async (
    values: EditCheckInFormValues,
  ): Promise<boolean> => {
    // Convert -1 ("could not assess") to null for the API
    const apiPainLevel =
      values.pain_level === -1 ? null : values.pain_level;

    const { error } = await finalizeCheckIn({
      matterId,
      checkInId: checkIn.id,
      payload: {
        check_in_id: checkIn.id,
        general_notes: values.general_notes,
        pain_level: apiPainLevel,
        pain_level_note: values.pain_level_note,
        updates: values.provider_updates.map((p) =>
          formProviderUpdateToApiProviderUpdate(p),
        ),
        objectives: values.objectives.map(formObjectiveToApiObjective),
      },
    });

    if (error) {
      toast.error('Failed to submit check-in');
      return false;
    }

    toast.success('Check-in submitted');
    await pushCheckInChanges({ generalNotes: values.general_notes });

    // If no external task linked but matter has a CMS, offer to push a note
    if (!hasLinkedExternalTasks && cmsResolution) {
      await promptPushNote({
        matterId,
        rawContent: buildCheckInContext(values),
        triggerType: 'task_complete',
        cmsResolution,
        taskTitle: 'Treatment Check-in',
        matterName: matter?.name,
        sourceType: 'TREATMENT_CHECK_IN',
        sourceId: checkIn.id,
      });
    }

    viewCheckIn();
    return true;
  };

  const handleApproveReviewProvider = async (providerId: string) => {
    try {
      // Approve the provider
      await approveMedicalProvider({ matterId, providerId }).unwrap();

      // Check if provider is already in this check-in
      const existing = providerUpdateFields.some(
        (p) => p.providerId === providerId,
      );
      if (existing) {
        toast.success('Provider approved (already in check-in)');
        return;
      }

      // Find the provider name from reviewFoundProviders
      const reviewedProvider = reviewFoundProviders.find(
        (rfp) => rfp.medical_provider_id === providerId,
      );
      const providerName = reviewedProvider?.name ?? null;

      // Create a provider update for this check-in
      const created = await addProviderUpdate({
        matterId,
        checkInId: checkIn.id,
        providerUpdate: {
          check_in_id: checkIn.id,
          provider_id: providerId,
          provider_name: providerName,
        },
      }).unwrap();

      // Add to the form
      appendProviderUpdate(apiProviderUpdateToFormProviderUpdate(created));

      // Generate objectives for the newly approved provider
      try {
        const newObjectives = await generateObjectives({
          matterId,
          checkInId: checkIn.id,
        }).unwrap();
        const merged = mergeServerObjectives(
          form.getValues('objectives'),
          newObjectives,
        );
        if (merged.length > form.getValues('objectives').length) {
          replaceObjectives(merged);
        }
      } catch {
        // Objectives will still sync via the refetch effect
      }

      toast.success('Provider approved and added to check-in');
    } catch {
      toast.error('Failed to approve provider');
    }
  };

  const handleRejectReviewProvider = async (providerId: string) => {
    try {
      await rejectMedicalProvider({ matterId, providerId }).unwrap();
      toast.success('Provider rejected');
    } catch {
      toast.error('Failed to reject provider');
    }
  };

  const handleCallClient = async () => {
    // Get the first client's first phone number
    const clientPhoneNumber =
      matter?.clients?.[0]?.phone_numbers?.[0]?.phone_number;

    if (!clientPhoneNumber) {
      toast.error('No phone number found for client');
      return;
    }

    try {
      if (isRingCentral) {
        if (!isRCConnected) {
          toast.error('RingCentral is not connected for your user');
          return;
        }

        const result = await initiateRCCall({
          matterId,
          to_number: clientPhoneNumber,
          check_in_id: checkIn.id,
        }).unwrap();

        window.location.href = result.call_uri;
        toast.success('Opening RingCentral app...');
        return;
      }

      await initiateDialpadCall({
        matterId,
        to_number: clientPhoneNumber,
        check_in_id: checkIn.id,
      }).unwrap();

      toast.success('Call initiated successfully');
    } catch {
      toast.error('Failed to initiate call');
    }
  };

  // Build a map from providerId → provider_updates index for objective data syncing
  const providerIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    providerUpdateFields.forEach((field, idx) => {
      if (field.providerId) {
        map.set(field.providerId, idx);
      }
    });
    return map;
  }, [providerUpdateFields]);

  // Providers completed in previous treatments — their treatment_status on the
  // matter is already COMPLETED before this check-in started.
  const previouslyCompletedProviderIds = useMemo(
    () =>
      new Set(
        medicalProviders
          .filter((p) => p.treatment_status === ProviderTreatmentStatus.COMPLETED)
          .map((p) => p.id),
      ),
    [medicalProviders],
  );


  const [inspectingProvider, setInspectingProvider] =
    useState<ReviewFoundProviderEntry | null>(null);

  if (checkIn.status === TreatmentCheckInStatus.COMPLETED) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <Subheading className="!text-lg/7 text-zinc-950">
          Check-in already submitted
        </Subheading>
        <Text>
          This check-in has already been completed and cannot be edited.
        </Text>
        <Button onClick={viewCheckIn}>View Check-in</Button>
      </div>
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Top Bar: Last Check-in Info + Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {checkIn.context?.days_since_last_check_in != null &&
                (() => {
                  const days = checkIn.context.days_since_last_check_in;
                  const getTimeText = (dayCount: number): string => {
                    if (dayCount === 0) return 'earlier today';
                    if (dayCount === 1) return '1 day ago';
                    return `${dayCount} days ago`;
                  };
                  return <span>Last check-in: {getTimeText(days)}</span>;
                })()}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCallClient}
                disabled={
                  isInitiatingCall ||
                  (isRingCentral && !isRCConnected) ||
                  !matter?.clients?.[0]?.phone_numbers?.[0]?.phone_number
                }
                size="sm"
              >
                {isInitiatingCall ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Calling...
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-1.5" />
                    Call Client
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={() => setIsFlagIssueModalOpen(true)}
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Flag className="h-4 w-4 mr-1.5" />
                Flag Issue
              </Button>
            </div>
          </div>

          {/* Last Check-in Overview */}
          <LastCheckInOverview matterId={matterId} structuredObjectivesEnabled />

          {/* Pain Level */}
          <PainLevelSelector hasError={validationErrors.has('pain_level')} />

          {/* Objectives Section (review providers + grouped objectives + notes + add provider) */}
          <ObjectivesSection
            isLoading={isGeneratingObjectives}
            objectiveFields={objectiveFields}
            providerIndexMap={providerIndexMap}
            reviewFoundProviders={reviewFoundProviders}
            onApproveProvider={handleApproveReviewProvider}
            onRejectProvider={handleRejectReviewProvider}
            isApprovingProvider={isApprovingProvider}
            isRejectingProvider={isRejectingProvider}
            onAddProvider={handleAddProvider}
            validationErrors={validationErrors}
            previouslyCompletedProviderIds={previouslyCompletedProviderIds}
            removeObjective={removeObjective}
            removeProviderUpdate={removeProviderUpdate}
            matterId={matterId}
          />

          {/* General Notes */}
          <div className="bg-white rounded-lg border p-3">
            <FormField
              control={form.control}
              name="general_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">
                    General Notes
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={3}
                      placeholder="Capture any high-level notes from the conversation..."
                      className="text-sm"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end items-center gap-2">
            {isSaving && (
              <p className="text-xs text-gray-500 mr-4 self-center">
                Saving...
              </p>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm">
              <Sparkles className="h-4 w-4 mr-1.5" />
              Log Check-in
            </Button>
          </div>
        </form>
      </Form>

      <AddProviderModal
        isOpen={isAddProviderModalOpen}
        onClose={() => setIsAddProviderModalOpen(false)}
        matterId={matterId}
        centerLat={incidentCenter?.lat}
        centerLng={incidentCenter?.lng}
        onProviderCreated={async (providerId, providerName) => {
          const existing = providerUpdateFields.some(
            (p) => p.providerId === providerId,
          );
          if (existing) {
            toast.info('That provider is already in this check-in.');
            setIsAddProviderModalOpen(false);
            return;
          }

          try {
            const created = await addProviderUpdate({
              matterId,
              checkInId: checkIn.id,
              providerUpdate: {
                check_in_id: checkIn.id,
                provider_id: providerId,
                provider_name: providerName ?? null,
              },
            }).unwrap();

            appendProviderUpdate(
              apiProviderUpdateToFormProviderUpdate(created),
            );
            setIsAddProviderModalOpen(false);

            // Generate objectives for the newly added provider and merge
            // them into the form immediately (the cache invalidation from
            // addProviderUpdate will also refetch, but this is faster).
            try {
              const newObjectives = await generateObjectives({
                matterId,
                checkInId: checkIn.id,
              }).unwrap();
              const merged = mergeServerObjectives(
                form.getValues('objectives'),
                newObjectives,
              );
              if (merged.length > form.getValues('objectives').length) {
                replaceObjectives(merged);
              }
            } catch {
              // Objectives will still sync via the refetch effect
            }
          } catch {
            toast.error('Failed to add provider to check-in');
          }
        }}
      />

      <FlagIssueModal
        checkInId={checkIn.id}
        open={isFlagIssueModalOpen}
        onOpenChange={setIsFlagIssueModalOpen}
      />


      {/* Provider Inspection Modal */}
      <Dialog
        open={!!inspectingProvider}
        onOpenChange={(open) => !open && setInspectingProvider(null)}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Provider Mention Details
            </DialogTitle>
            <DialogDescription>
              Evidence and context for this provider mention from source
              documents
            </DialogDescription>
          </DialogHeader>

          {inspectingProvider && (
            <div className="space-y-4 mt-4">
              {/* Provider Information - Gray */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-sm text-gray-900 mb-2">
                  Provider Information
                </h3>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{' '}
                    {inspectingProvider.name || 'Unknown'}
                  </div>
                  {inspectingProvider.latest_mention?.specialty_hint && (
                    <div>
                      <span className="font-medium">Specialty:</span>{' '}
                      {inspectingProvider.latest_mention.specialty_hint}
                    </div>
                  )}
                  {inspectingProvider.latest_mention?.location_hint && (
                    <div className="flex items-start gap-1">
                      <MapPin className="h-4 w-4 mt-0.5 text-gray-600 flex-shrink-0" />
                      <span>
                        {inspectingProvider.latest_mention.location_hint}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Source Document - Blue */}
              {inspectingProvider.latest_mention?.artifact_id && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-sm text-blue-900 mb-2 flex items-center gap-1">
                    Source Document
                    <ExternalLink className="h-3.5 w-3.5" />
                  </h3>
                  <div className="space-y-1 text-sm text-blue-900">
                    <div>
                      <span className="font-medium">Type:</span>{' '}
                      {inspectingProvider.latest_mention.artifact_type}
                    </div>
                    <div>
                      <span className="font-medium">Artifact ID:</span>{' '}
                      <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">
                        {inspectingProvider.latest_mention.artifact_id}
                      </code>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Reasoning - Purple */}
              {inspectingProvider.latest_mention?.reasoning && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="font-semibold text-sm text-purple-900 mb-2">
                    AI Analysis
                  </h3>
                  <p className="text-sm text-purple-900 whitespace-pre-wrap">
                    {inspectingProvider.latest_mention.reasoning}
                  </p>
                </div>
              )}

              {/* Excerpts - Gray */}
              {inspectingProvider.latest_mention?.excerpts &&
                inspectingProvider.latest_mention.excerpts.length > 0 && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-sm text-gray-900 mb-2">
                      Text Excerpts
                    </h3>
                    <ul className="space-y-2">
                      {inspectingProvider.latest_mention.excerpts.map(
                        (excerpt: string, idx: number) => (
                          <li
                            key={idx}
                            className="text-sm text-gray-700 italic border-l-2 border-gray-300 pl-3"
                          >
                            &ldquo;{excerpt}&rdquo;
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {/* Encounters Mentioned - Green */}
              {inspectingProvider.latest_mention?.encounters_mentioned &&
                inspectingProvider.latest_mention.encounters_mentioned.length >
                  0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-sm text-green-900 mb-2">
                      Encounters Mentioned
                    </h3>
                    <ul className="space-y-1.5">
                      {inspectingProvider.latest_mention.encounters_mentioned.map(
                        (encounter: EncounterSchema, idx: number) => (
                          <li key={idx} className="text-sm text-green-900">
                            • {encounter.date || 'Unknown date'}
                            {encounter.services &&
                              encounter.services.length > 0 &&
                              ` - ${encounter.services.join(', ')}`}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {/* Confidence Score */}
              {inspectingProvider.latest_mention?.confidence != null && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-semibold text-sm text-yellow-900 mb-2">
                    AI Confidence Score
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-yellow-100 rounded-full h-2">
                      <div
                        className="bg-yellow-600 h-2 rounded-full"
                        style={{
                          width: `${inspectingProvider.latest_mention.confidence * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-yellow-900">
                      {Math.round(
                        inspectingProvider.latest_mention.confidence * 100,
                      )}
                      %
                    </span>
                  </div>
                </div>
              )}

              {/* Citations */}
              {inspectingProvider.latest_mention?.citation_ids &&
                inspectingProvider.latest_mention.citation_ids.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-sm text-blue-900 mb-2">
                      Citations
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {inspectingProvider.latest_mention.citation_ids.map(
                        (citationId: string) => (
                          <Badge
                            key={citationId}
                            variant="secondary"
                            className="text-xs"
                          >
                            {citationId}
                          </Badge>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
